<template>
  <div class="fm-file-slider-pdf">
    <div class="fm-file-slider-pdf__viewport">
      <div v-if="pdfLoading" class="fm-file-slider-pdf__loading">
        <ProgressSpinner />
      </div>
      <FMFileViewerError v-else-if="pdfError != null" />
      <Transition v-else-if="currentResolvedPage != null" name="fm-pdf-page" mode="out-in">
        <FMFileViewerPDFPageCanvas
          :key="`pdfPage${currentResolvedPage}`"
          :pdf="pdfDoc"
          :page-number="currentResolvedPage"
          fit-mode="contain"
          :scale="1"
          :active="true"
          :big-shadow="bigShadow"
          :keep-rendered="false"
          :recently-changed="recentlyChanged"
        />
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import ProgressSpinner from "primevue/progressspinner";
import FMFileViewerPDFPageCanvas from "./FMFileViewerPDFPageCanvas.vue";
import FMFileViewerError from "../FMFileViewerError.vue";
import { usePdfDocument } from "./usePdfDocument";

const props = defineProps({
  fileUUID: {
    type: String,
    required: true,
  },
  visibleTimeInSeconds: {
    type: Number,
    required: true,
  },
  customPagesOrder: {
    type: Array as () => number[] | undefined,
    required: false,
    default: undefined,
  },
  recentlyChanged: {
    type: Boolean,
    required: false,
    default: false,
  },
  bigShadow: {
    type: Boolean,
    required: false,
    default: false,
  },
  active: {
    type: Boolean,
    required: false,
    default: true,
  },
});

const emit = defineEmits<{
  "request-next": [];
}>();

const sequenceIndex = ref(0);
let pageTimeout: ReturnType<typeof setTimeout> | null = null;

const { pdfLoading, pdfError, pageCount, pdfDoc } = usePdfDocument(computed(() => props.fileUUID));

const allPages = computed<number[]>(() => {
  const maxPage = pageCount.value;

  if (maxPage <= 0) {
    return [];
  }

  return Array.from({ length: maxPage }, (_, index) => index + 1);
});

const resolvedPages = computed<number[]>(() => {
  if (allPages.value.length === 0) {
    return [];
  }

  const configured = props.customPagesOrder;

  if (configured == null || configured.length === 0) {
    return allPages.value;
  }

  const validConfiguredPages = configured.filter(
    (page) => Number.isInteger(page) && page >= 1 && page <= pageCount.value,
  );

  return validConfiguredPages.length > 0 ? validConfiguredPages : allPages.value;
});

const currentResolvedPage = computed<number | null>(() => {
  return resolvedPages.value[sequenceIndex.value] ?? null;
});

function clearPageTimeout() {
  if (pageTimeout != null) {
    clearTimeout(pageTimeout);
    pageTimeout = null;
  }
}

function schedulePageAdvance() {
  clearPageTimeout();

  if (!props.active || pdfLoading.value) {
    return;
  }

  pageTimeout = setTimeout(
    () => {
      if (pdfError.value != null) {
        emit("request-next");
        return;
      }

      if (sequenceIndex.value < resolvedPages.value.length - 1) {
        sequenceIndex.value += 1;
        return;
      }

      emit("request-next");
    },
    Math.max(1, props.visibleTimeInSeconds || 1) * 1000,
  );
}

watch(
  () => props.fileUUID,
  () => {
    sequenceIndex.value = 0;
  },
);

watch(
  () => [pageCount.value, props.customPagesOrder] as const,
  () => {
    if (sequenceIndex.value >= resolvedPages.value.length) {
      sequenceIndex.value = 0;
    }
  },
  { deep: true },
);

watch(
  () =>
    [
      props.visibleTimeInSeconds,
      props.active,
      sequenceIndex.value,
      resolvedPages.value.join(","),
      pdfLoading.value,
      pdfError.value != null,
    ] as const,
  () => {
    schedulePageAdvance();
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  clearPageTimeout();
});
</script>

<style lang="scss" scoped>
.fm-file-slider-pdf {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
}

.fm-file-slider-pdf__viewport {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(0.225rem, 1cqi, 2rem);
}

.fm-file-slider-pdf__viewport :deep(.pdf-page-canvas) {
  width: 100%;
  height: 100%;
}

.fm-file-slider-pdf__viewport :deep(.pdf-page-canvas canvas) {
  max-width: 100%;
  max-height: 100%;
}

.fm-file-slider-pdf__viewport :deep(.pdf-page-canvas canvas) {
  max-width: 100%;
  max-height: 100%;
}

.fm-file-slider-pdf__loading {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fm-file-slider-pdf__viewport {
  position: relative;
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.fm-file-slider-pdf__viewport :deep(.pdf-page-canvas) {
  width: 100%;
  height: 100%;
}

.fm-file-slider-pdf__viewport :deep(.pdf-page-canvas canvas) {
  max-width: 100%;
  max-height: 100%;
}

.fm-pdf-page-enter-active,
.fm-pdf-page-leave-active {
  transition:
    transform 0.4s ease,
    opacity 0.4s ease;
}

.fm-pdf-page-enter-from {
  transform: translateY(10%);
  opacity: 0;
}

.fm-pdf-page-enter-to {
  transform: translateY(0);
  opacity: 1;
}

.fm-pdf-page-leave-from {
  transform: translateY(0);
  opacity: 1;
}

.fm-pdf-page-leave-to {
  transform: translateY(-10%);
  opacity: 0;
}
</style>
